import { useAtom, useAtomValue } from 'jotai';
import { ArrowRight, ShieldAlert, TriangleAlert, WifiOff } from 'lucide-react';
import { Badge, Card, CardContent } from '@higgins/ui';
import { meAtom, settingsOpenAtom } from '../store/atoms';
import { TileIcon } from '../components/tile-icon';
import type { AppStatus, MeResponse } from '../lib/api';
import SettingsPage from './settings';

/**
 * Testers should know they're not on the live version. Live apps get no badge.
 * The words match the status names on the Settings page.
 */
const STAGE_BADGE: Partial<Record<AppStatus, string>> = { dev: 'Dev', uat: 'UAT' };

type Tile = MeResponse['apps'][number];

function AppTile({ app, href }: { app: Tile; href: string }) {
  return (
    <a
      href={href}
      className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-orange-300 hover:shadow-md active:bg-slate-50"
    >
      <div className="flex items-start gap-3">
        <TileIcon icon={app.icon} colour={app.colour} />
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-2">
            <h2 className="font-semibold text-slate-900">
              {app.name}
              {STAGE_BADGE[app.status] && (
                <Badge variant="outline" className="ml-2 align-middle text-[10px] font-medium">
                  {STAGE_BADGE[app.status]}
                </Badge>
              )}
            </h2>
            <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-orange-500" />
          </span>
          <p className="mt-1 text-sm leading-snug text-slate-600">{app.description}</p>
        </span>
      </div>
    </a>
  );
}

/** No entitlements: a request-access explainer, never a wall or an error. */
function EmptyState({ upn }: { upn: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <ShieldAlert className="h-10 w-10 text-orange-500" />
        <p className="font-semibold text-slate-900">No apps yet</p>
        <p className="max-w-md text-sm text-slate-600">
          You&apos;re signed in as <strong>{upn}</strong>, but you haven&apos;t been given
          access to any apps. Contact IT and mention which tools your role needs —
          access is granted by team, so it&apos;s usually a quick add.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * The live app list couldn't be read, so the API used its built-in copy, which
 * can be out of date and hide an app this person is entitled to (#260).
 */
function FallbackNotice() {
  return (
    <p role="status" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        Couldn&apos;t load the latest app list, so some apps may be missing. Refresh the
        page, or tell IT if it keeps happening.
      </span>
    </p>
  );
}

/** No signal: these are the tiles from last time, and only some apps open offline (#391). */
function OfflineNotice() {
  return (
    <p role="status" className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
      <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        No signal — these are your apps from last time. Only apps that work offline (Measure to Quote) will open
        until you&apos;re back online.
      </span>
    </p>
  );
}

export default function HomePage() {
  const me = useAtomValue(meAtom);
  // Single-page app by design — the IT/Dev settings view is a state toggle
  // driven from the header gear (_layout), not a route. The gear renders only
  // for HigginsApps-IT and -Dev (server-verified again on every /api/catalogue call).
  const [settingsOpen, setSettingsOpen] = useAtom(settingsOpenAtom);

  if (settingsOpen) return <SettingsPage onBack={() => setSettingsOpen(false)} />;
  const notice = me.offline ? <OfflineNotice /> : me.usingFallback && <FallbackNotice />;
  if (me.apps.length === 0 && !me.canManage) {
    return (
      <div className="space-y-4">
        {notice}
        <EmptyState upn={me.upn} />
      </div>
    );
  }

  // SSO hand-off (#162): tiles carry the signed-in UPN as login_hint so each
  // app's own MSAL redirect rides the existing Entra session silently instead
  // of prompting (the documented pattern for SSO between first-party apps).
  // EasyAuth apps simply ignore the parameter.
  const withHint = (url: string) =>
    `${url}${url.includes('?') ? '&' : '?'}login_hint=${encodeURIComponent(me.upn)}`;

  // Staging sites (second copies for testing) sit in their own section below,
  // so testers who see both copies can tell them apart.
  const apps = me.apps.filter((a) => !a.stagingSite);
  const staging = me.apps.filter((a) => a.stagingSite);

  return (
    <div className="space-y-4">
      {notice}
      {apps.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {apps.map((app) => <AppTile key={app.key} app={app} href={withHint(app.url)} />)}
        </div>
      )}
      {staging.length > 0 && (
        <section aria-labelledby="staging-heading" className="space-y-3 border-t border-slate-200 pt-4">
          <div>
            <h2 id="staging-heading" className="font-semibold text-slate-900">Staging Sites</h2>
            <p className="text-sm text-slate-600">Copies of apps for testing. Real work goes in the apps above.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {staging.map((app) => <AppTile key={app.key} app={app} href={withHint(app.url)} />)}
          </div>
        </section>
      )}
    </div>
  );
}
